import React, { useEffect, useState, useCallback } from 'react';
import { Navbar } from '../components/Navbar';
import { useAuth } from '../contexts/AuthContext';
import { api, formatApiError } from '../lib/api';
import { KeyRound } from 'lucide-react';
import { toast } from 'sonner';

const BUSINESS_TYPE_LABEL = {
  funeral_home: 'Funeral home',
  cemetery: 'Cemetery',
  other: 'Professional partner',
};

export default function ProfessionalDashboard() {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pin, setPin] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem('myleaf_pro_unlocked') === '1');

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/professional/me');
      setInfo(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const verifyPin = async (e) => {
    e.preventDefault();
    if (!pin) return;
    setUnlocking(true);
    try {
      await api.post('/professional/access/verify', { pin: pin.trim() });
      sessionStorage.setItem('myleaf_pro_unlocked', '1');
      setUnlocked(true);
      toast.success('Welcome — your dashboard is now open.');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUnlocking(false);
    }
  };

  if (loading || !info) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="ml-container py-20 text-center text-[hsl(var(--primary)/0.6)]" data-testid="pro-dashboard-loading">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="ml-container py-12 space-y-8" data-testid="professional-dashboard">
        <header>
          <div className="ml-overline">Professional</div>
          <h1 className="font-serif text-4xl mt-1 text-[hsl(var(--primary))]">
            {info.business_name || user?.name}
          </h1>
          <p className="mt-2 text-sm text-[hsl(var(--primary)/0.78)] font-medium">
            {BUSINESS_TYPE_LABEL[info.business_type] || 'Professional account'}
          </p>
        </header>

        {/* Status — calm, premium, no technical phrasing */}
        {info.status === 'pending' && (
          <StatusCard
            tone="pending"
            title="We are reviewing your application"
            body="Thank you for applying. Our team typically responds within one to two business days. We will be in touch by email at the address on file."
            testId="pro-status-pending"
          />
        )}

        {info.status === 'rejected' && (
          <StatusCard
            tone="rejected"
            title="We are not able to move forward at this time"
            body={
              info.rejection_reason
                ? info.rejection_reason
                : 'Our team has reviewed your application. If you believe this was in error, please reach out to admin@myleaflegacy.info and we will take another look.'
            }
            testId="pro-status-rejected"
          />
        )}

        {/* Approved: PIN gate */}
        {info.status === 'approved' && info.has_access_pin && !unlocked && (
          <section className="ml-card max-w-md" data-testid="pro-pin-gate">
            <div className="flex items-center gap-3">
              <KeyRound className="w-5 h-5 text-[hsl(var(--accent))]" />
              <h2 className="font-serif text-2xl text-[hsl(var(--primary))]">Welcome — please enter your access code</h2>
            </div>
            <p className="mt-3 text-sm text-[hsl(var(--primary)/0.78)] font-medium leading-relaxed">
              We have emailed your six-digit access code to <strong>{user?.email}</strong>. Enter it once to open your dashboard.
            </p>
            <form onSubmit={verifyPin} className="mt-5 space-y-3 wizard-readable">
              <input
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
                placeholder="6-digit code"
                className="ml-input font-mono tracking-[0.4em] text-center"
                inputMode="numeric"
                autoFocus
                aria-label="Access code"
                data-testid="pro-pin-input"
              />
              <button
                type="submit"
                disabled={unlocking || !pin}
                className="ml-btn-primary w-full !py-3"
                data-testid="pro-pin-submit"
              >
                {unlocking ? 'Opening…' : 'Open my dashboard'}
              </button>
              <p className="text-[11px] text-[hsl(var(--primary)/0.6)] font-medium">
                Didn't receive it? Check spam, or write to admin@myleaflegacy.info.
              </p>
            </form>
          </section>
        )}

        {/* Approved + unlocked: clean dashboard */}
        {info.status === 'approved' && unlocked && (
          <ApprovedView info={info} user={user} />
        )}

        {/* Account summary (always visible) */}
        <section className="ml-card space-y-3" data-testid="pro-account-summary">
          <div className="ml-overline">On file</div>
          <SummaryRow label="Legal business name" value={info.legal_business_name || '—'} />
          <SummaryRow
            label="Tax ID"
            value={info.tax_id_last4 ? `••• ••• ${info.tax_id_last4}` : '—'}
            testId="pro-tax-masked"
          />
          <SummaryRow label="Contact email" value={user?.email} />
        </section>
      </div>
    </div>
  );
}

/* -------- Approved (clean, premium) -------- */

function ApprovedView({ info, user }) {
  return (
    <>
      <section
        className="rounded-md bg-[hsl(var(--primary))] text-[#F4E5C2] px-6 py-7 sm:px-9 sm:py-9 shadow-[0_30px_60px_-30px_rgba(26,54,38,0.35)]"
        data-testid="pro-unlocked"
      >
        <div className="ml-overline !text-[#D9B775] !font-semibold">Welcome aboard</div>
        <h2 className="font-serif text-3xl sm:text-4xl mt-3 leading-tight font-semibold">
          You're approved, {(user?.name || '').split(' ')[0]}.
        </h2>
        <p className="mt-4 max-w-2xl text-[#FDFBF7]/85 font-medium leading-relaxed">
          {info.business_name} can now help families create memorials in person, hand out plaque kits, and reach our priority support line.
        </p>
      </section>

      <div className="grid gap-5 sm:grid-cols-3">
        <FeatureCard
          title="Help families on site"
          body="Walk a family through their first memorial setup, right from your premises."
          testId="pro-feature-onsite"
        />
        <FeatureCard
          title="Order plaque kits"
          body="QR plaques and Forever Here Collections shipped to your address for in-person handoff."
          testId="pro-feature-plaques"
        />
        <FeatureCard
          title="Priority support"
          body={
            <>
              Reach us at{' '}
              <a
                href="mailto:admin@myleaflegacy.info"
                className="text-[hsl(var(--accent))] hover:underline"
              >
                admin@myleaflegacy.info
              </a>
              .
            </>
          }
          testId="pro-feature-support"
        />
      </div>
    </>
  );
}

/* -------- Reusable bits -------- */

function StatusCard({ tone, title, body, testId }) {
  const borderClass =
    tone === 'rejected'
      ? 'border-l-[hsl(var(--destructive))]'
      : 'border-l-[hsl(var(--accent))]';
  return (
    <section className={`ml-card border-l-4 ${borderClass}`} data-testid={testId}>
      <h2 className="font-serif text-2xl text-[hsl(var(--primary))]">{title}</h2>
      <p className="mt-3 text-sm text-[hsl(var(--primary)/0.78)] font-medium leading-relaxed">
        {body}
      </p>
    </section>
  );
}

function FeatureCard({ title, body, testId }) {
  return (
    <div
      className="ml-card !p-5"
      data-testid={testId}
    >
      <h3 className="font-serif text-lg text-[hsl(var(--primary))]">{title}</h3>
      <p className="mt-2 text-sm text-[hsl(var(--primary)/0.78)] font-medium leading-relaxed">{body}</p>
    </div>
  );
}

function SummaryRow({ label, value, testId }) {
  return (
    <div className="flex items-baseline justify-between gap-4 text-sm" data-testid={testId}>
      <div className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[hsl(var(--primary)/0.6)]">{label}</div>
      <div className="font-medium text-[hsl(var(--primary))] text-right truncate">{value}</div>
    </div>
  );
}
